import type { TOC } from '@ember/component/template-only';
import { joinList } from '../../utils/format';
import { openLibraryUrl } from '../../utils/external-urls';

interface Signature {
  Args: {
    data: Record<string, unknown> | null | undefined;
    name?: string | null;
  };
}

interface BookData {
  title?: string;
  originalTitle?: string;
  authors?: string[];
  firstPublishYear?: number;
  pageCount?: number;
  coverUrl?: string;
  isbn?: string;
  subjects?: string[];
  openLibraryKey?: string;
}

function asBookData(
  raw: Record<string, unknown> | null | undefined,
  name?: string | null
): BookData {
  const d = (raw ?? {}) as BookData;
  d.authors = d.authors ?? [];
  d.subjects = d.subjects ?? [];
  // Only show originalTitle if it differs from the display name
  if (d.originalTitle && name && d.originalTitle === name) {
    d.originalTitle = undefined;
  }
  return d;
}

function topN(items: string[] | undefined, n: number): string[] {
  return (items ?? []).slice(0, n);
}

const BookDetail: TOC<Signature> = <template>
  {{#let (asBookData @data @name) as |d|}}
    <div class="flex flex-col gap-3">
      {{#if d.originalTitle}}
        <p class="text-base-content/60 text-xs italic">
          Original Title:
          {{d.originalTitle}}
        </p>
      {{/if}}

      {{#if d.authors.length}}
        <p class="text-base-content/80 text-sm font-medium">
          {{joinList d.authors}}
        </p>
      {{/if}}

      <div class="flex flex-wrap gap-2">
        {{#if d.firstPublishYear}}
          <span class="badge badge-outline">{{d.firstPublishYear}}</span>
        {{/if}}
        {{#if d.pageCount}}
          <span class="badge badge-ghost">{{d.pageCount}} pages</span>
        {{/if}}
        {{#if d.isbn}}
          <span class="badge badge-ghost badge-sm">ISBN {{d.isbn}}</span>
        {{/if}}
      </div>

      {{#if d.subjects.length}}
        <div class="flex flex-wrap gap-1">
          {{#each (topN d.subjects 5) as |subject|}}
            <span class="badge badge-outline badge-sm">{{subject}}</span>
          {{/each}}
        </div>
      {{/if}}

      {{#if d.openLibraryKey}}
        <div>
          <a
            href={{openLibraryUrl d.openLibraryKey}}
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-xs btn-outline"
          >
            Open Library ↗
          </a>
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default BookDetail;
